import { Theme } from "@deere/fuel-react";
import { SxProps, Typography } from "@mui/material";
import {
  gridClasses,
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRenderHeaderFilterProps,
  GridRowId,
} from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { Dispatch } from "react";
import { OrderLine } from "../../../../api/orders/orderInterfaces";
import DateRangePicker from "../../../../components/DatePickers/DateRangePicker/DateRangePicker";
import i18n from "../../../../i18n/i18n";
import { formatDate } from "../../../../utils/formatters";
import OrderNumberCell from "../../components/OrderNumberCell/OrderNumberCell";
import LineStatusBadge from "../../components/StatusBadge/LineStatusBadge";
import SubmittedDateCell from "../../components/SubmittedDateCell/SubmittedDateCell";
import WarningIconBadge from "../../components/WarningIconBadge/WarningIconBadge";
import {
  getInvestigationLineItemTooltip,
  getInvestigationTooltip,
  HELD_FOR_FUTURE_RELEASE_STATUS_CODE,
} from "../../tableUtils";
import OrdersByLineItemLineStatusFilter from "../components/OrdersByLineItemLineStatusFilter/OrdersByLineItemLineStatusFilter";
import OrdersByLineItemSearchFilter from "../components/OrdersByLineItemSearchFilter/OrdersByLineItemSearchFilter";
import ResourceLink from "../components/ResourceLink/ResourceLink";
import { OrdersByLineItemActions, setFilterAction } from "../reducers/actions";
import { LineStatus, OrderLineRow, OrdersByLineItemFilter } from "../types";

export const generateGridDefaultStyles = (theme: Theme): SxProps => ({
  border: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  [`.${gridClasses.overlayWrapper}`]: {
    height: "50vh",
    borderRadius: "10px",
  },
  [`.${gridClasses.overlayWrapperInner}`]: {
    height: "100% !important",
  },
  [`.${gridClasses.virtualScroller}`]: {
    overflow: "hidden",
    border: 0,
    overflowX: "scroll",
  },
  [`.${gridClasses.cell}`]: {
    padding: theme.tokens.FuelSpacing4,
    outline: "none !important",
    alignItems: "center",
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
    "& a svg": {
      marginTop: "auto",
      marginBottom: "auto",
    },
  },
  [`& .${gridClasses.row}`]: {
    maxHeight: "none !important",
    minHeight: "auto !important",
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
    "&:last-child": {
      borderBottom: 0,
    },
    "& > .MuiDataGrid-cell:first-of-type": {
      padding: "16px 4px",
    },
  },
  [`& .${gridClasses.toolbarContainer} > button`]: {
    border: "none",
    color: theme.tokens.FuelColorBrandPrimary,
    boxShadow: "none",
    margin: theme.tokens.FuelSpacing4,
  },
  "& .MuiDataGrid-cell.MuiDataGrid-cellEmpty": {
    padding: 0,
  },
  [`& .${gridClasses.toolbarContainer}`]: {
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
    height: "4.25rem",
  },
  [`& .${gridClasses.columnHeader}`]: {
    paddingLeft: theme.tokens.FuelSpacing4,
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
    "&:hover": {
      backgroundColor: theme.tokens.FuelColorGray50,
    },
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  },
  [`& .${gridClasses.columnSeparator}`]: {
    color: "transparent",
    transition: "color 0.2s",
  },
  [`& .${gridClasses.columnHeaders}:hover .${gridClasses.columnSeparator}`]: {
    color: theme.tokens.FuelColorBorderLight,
  },
  [`& .${gridClasses.columnHeaders}`]: {
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  },
  [`& .${gridClasses.columnHeaders} > div[role="row"]`]: {
    backgroundColor: theme.tokens.FuelColorBackgroundWhite,
  },
  [`& .${gridClasses.iconButtonContainer}`]: {
    marginX: theme.tokens.FuelSpacing1,
  },
  [`& .${gridClasses.row} > div`]: {
    alignItems: "flex-start",
    maxHeight: "none",
    minHeight: "auto",
    height: "auto",
  },
});

export const generateColumns = (
  theme: Theme,
  t: TFunction,
  filters: OrdersByLineItemFilter,
  toggles: {
    isPromotionsEnabled: boolean;
    isRBACEnabled: boolean;
    SLAColumnEnabled: boolean;
    isDateTimeCreated: boolean;
    isInvestigationMessagesEnabled: boolean;
  },
  isAllAccounts: boolean,
  tabIndex: number | undefined,
  dispatch: Dispatch<OrdersByLineItemActions>,
  onFilterChange?: (filters: OrdersByLineItemFilter) => void
): GridColDef[] =>
  (
    [
      ...(isAllAccounts
        ? [
            {
              field: "accountId",
              filterable: true,
              headerName: t("orders:allOrders.table.header.accountId"),
              renderHeaderFilter: () => <></>,
            },
          ]
        : []),
      {
        field: "orderNumber",
        headerName: t("orders:allOrders.table.header.orderNumber"),
        renderCell: (params: GridRenderCellParams) => (
          <OrderNumberCell value={params.value} row={params.row} />
        ),
        align: isAllAccounts ? "right" : "left",
        headerAlign: isAllAccounts ? "right" : "left",
        renderHeaderFilter: () => <></>,
      },
      {
        field: "orderType",
        headerName: t("orders:allOrders.table.header.orderType"),
        valueFormatter: (value: any) => value && t(`orders:orderType.${value}`),
        renderHeaderFilter: () => <></>,
      },
      {
        field: "partNumber",
        filterable: true,
        headerName: t("orders:allOrders.table.header.partNumber"),
        align: "right",
        headerAlign: "right",
        renderCell: (params: GridRenderCellParams) => {
          const row = params.api.getRow(params.id as GridRowId) as OrderLineRow;
          return (
            <ResourceLink
              field="parts"
              url={
                toggles.isRBACEnabled
                  ? `/parts/${encodeURIComponent(params.value)}`
                  : `/dealer/${row.accountId}/parts/${encodeURIComponent(
                      params.value
                    )}`
              }
              resourceValue={params.value}
            />
          );
        },
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <OrdersByLineItemSearchFilter
            inputRef={inputRef}
            field="partNumber"
            filters={filters}
            dispatch={dispatch}
            onFilterChange={onFilterChange}
          />
        ),
        minWidth: 175,
      },
      {
        field: "dealerReference",
        filterable: true,
        headerName: t("orders:allOrders.table.header.dealerReference"),
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <OrdersByLineItemSearchFilter
            inputRef={inputRef}
            field="dealerReference"
            filters={filters}
            dispatch={dispatch}
          />
        ),
        minWidth: 175,
      },
      ...(toggles.isDateTimeCreated
        ? [
            {
              field: "createdAt",
              filterable: true,
              sortable: true,
              headerName: t("orders:allOrders.table.header.dateTimeCreated"),
              renderCell: (params: GridRenderCellParams) => (
                <SubmittedDateCell date={params.value} />
              ),
              renderHeaderFilter: () => (
                <DateRangePicker
                  label=""
                  dateRangeCalendarProps={{
                    disableFuture: true,
                    minDate: dayjs().subtract(1, "year"),
                  }}
                  enableShortcuts
                  onChange={([from, to]) => {
                    dispatch(
                      setFilterAction({
                        ...filters,
                        latestOrderCreationDateFrom: from,
                        latestOrderCreationDateTo: to,
                      })
                    );
                  }}
                  value={[
                    filters.latestOrderCreationDateFrom ?? null,
                    filters.latestOrderCreationDateTo ?? null,
                  ]}
                />
              ),
              minWidth: 300,
            },
          ]
        : [
            {
              field: "submittedAt",
              headerName: t("orders:allOrders.table.header.submittedDate"),
              renderCell: (params: GridRenderCellParams) => (
                <SubmittedDateCell date={params.value} />
              ),
              type: "date",
              valueGetter: (value: any) =>
                value ? new Date(value) : undefined,
              renderHeaderFilter: () => <></>,
            },
          ]),
      ...(toggles.isPromotionsEnabled
        ? [
            {
              field: "promotionId",
              filterable: true,
              headerName: t("orders:orderEntry.summary.header.promotionId"),
              align: "right",
              headerAlign: "right",
              renderCell: (params: GridRenderCellParams) => {
                const row = params.api.getRow(
                  params.id as GridRowId
                ) as OrderLineRow;
                return (
                  <ResourceLink
                    field="promotions"
                    url={`/promotions/${params.value}`}
                    state={{ dealerAccountId: row.accountId }}
                    resourceValue={params.value}
                  />
                );
              },
              renderHeaderFilter: ({
                inputRef,
              }: GridRenderHeaderFilterProps) => (
                <OrdersByLineItemSearchFilter
                  inputRef={inputRef}
                  field="promotionId"
                  filters={filters}
                  dispatch={dispatch}
                />
              ),
              minWidth: 175,
            },
          ]
        : []),
      {
        field: "promiseDate",
        type: "date",
        headerName: t("orders:allOrders.table.header.promiseDate"),
        valueFormatter: (value: any) =>
          value ? formatDate(value, i18n.language, false) : "",
        renderHeaderFilter: () => <></>,
        minWidth: 200,
      },
      {
        field: "backOrderedDate",
        headerName: t("orders:allOrders.table.header.backOrderedDate"),
        type: "date",
        valueFormatter: (value: any) =>
          value ? formatDate(value, i18n.language, false) : "",
        renderHeaderFilter: () => <></>,
      },
      {
        field: "shipDate",
        headerName: t("orders:allOrders.table.header.shipDate"),
        type: "date",
        valueFormatter: (value: any) =>
          value ? formatDate(value, i18n.language, false) : "",
        renderHeaderFilter: () => <></>,
      },
      {
        field: "shippingUnit",
        headerName: t("orders:allOrders.table.header.shippingUnit"),
        valueFormatter: (value: any) =>
          value &&
          (i18n.exists(`parts:parts.location.${value}`)
            ? t(`parts:parts.location.${value}`)
            : value),
        renderHeaderFilter: () => <></>,
      },
      ...(toggles.SLAColumnEnabled
        ? [
            {
              field: "serviceLevelAgreement",
              headerName: t(
                "orders:allOrders.table.header.serviceLevelAgreement"
              ),
              renderCell: (params: GridRenderCellParams) => {
                const { value } = params;
                return (
                  <Typography data-testid="service-level-agreement">
                    {i18n.exists(`orders:serviceLevel.${value}`)
                      ? t(`orders:serviceLevel.${value}`)
                      : value}
                  </Typography>
                );
              },
              renderHeaderFilter: () => <></>,
            },
          ]
        : []),
      {
        field: "investigation",
        headerName: t("orders:allOrders.table.header.investigation"),
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const hasHeldForFutureReleaseCode =
            params.row.lineStatusCodes?.includes(
              HELD_FOR_FUTURE_RELEASE_STATUS_CODE
            ) ?? false;

          if (params.value || hasHeldForFutureReleaseCode) {
            const investigation =
              params.value || LineStatus.DEERE_INVESTIGATION.toString();
            const isDealerInvestigation =
              investigation === LineStatus.DEALER_INVESTIGATION.toString();
            return (
              <WarningIconBadge
                title={
                  toggles.isInvestigationMessagesEnabled
                    ? getInvestigationLineItemTooltip(
                        investigation,
                        params.row.lineStatusCodes,
                        theme,
                        t
                      )
                    : getInvestigationTooltip(
                        hasHeldForFutureReleaseCode
                          ? HELD_FOR_FUTURE_RELEASE_STATUS_CODE
                          : investigation,
                        false,
                        t
                      )
                }
                iconName={isDealerInvestigation ? "warning" : "info"}
              />
            );
          }
          return <></>;
        },
        renderHeaderFilter: () => <></>,
      },
      {
        field: "lineStatus",
        filterable: true,
        headerName: t("orders:allOrders.table.header.lineStatus"),
        renderCell: (params: GridRenderCellParams) => {
          if (params.value) return <LineStatusBadge status={params.value} />;
          return <></>;
        },
        renderHeaderFilter: () => (
          <OrdersByLineItemLineStatusFilter
            dispatch={dispatch}
            filters={filters}
            tabIndex={tabIndex}
          />
        ),
        minWidth: 200,
      },
    ] as GridColDef[]
  ).map((column) => ({
    headerAlign: "left",
    align: "left",
    minWidth: 150,
    flex: 1,
    ...(column.filterable && {
      renderHeader: (params: GridColumnHeaderParams) => (
        <>
          <Typography
            sx={{
              fontWeight: theme.tokens.FuelFontWeightSemibold,
              fontSize: theme.tokens.FuelFontSizeHeading6,
              marginRight: theme.tokens.FuelSpacing1,
            }}
            data-testid={params.field}
          >
            {params.colDef.headerName}
          </Typography>
        </>
      ),
    }),
    ...column,
  }));

export const generateRows = (orderLines: OrderLine[]): OrderLineRow[] =>
  orderLines.map((line, index) => ({
    orderNumber: line.orderNumber,
    orderStatus: line.orderStatus,
    investigation: line.investigation,
    lineStatusCodes: line.lineStatusCodes,
    dealerReference: line.dealerReference,
    partNumber: line.partNumber,
    orderType: line.orderType,
    promotionId: line.promotionId,
    lineStatus: line.lineStatus,
    promiseDate: line.promiseDate,
    backOrderedDate: line.backOrderedDate,
    shipDate: line.shipDate,
    shippingUnit: line.shippingUnit,
    serviceLevelAgreement: line.serviceLevelAgreement,
    submittedAt: line.submittedAt,
    id: `orderLine-${line.orderNumber}-${line.partNumber}-${index}`,
    accountId: line.accountId,
    createdAt: line.createdAtZoned,
  }));

export const getTooltipTextForDownloadButton = (
  orderLines: OrderLine[],
  isFilterApplied: boolean,
  isDownloading: boolean,
  t: TFunction
) => {
  if (orderLines.length === 0 && !isFilterApplied) return "";
  if (isDownloading) return t("orders:download.disabled.toolTip");
  return t("orders:download.toolTip");
};
